// Deterministic sample leaf facts — every aggregate on screen rolls up from these atomic rows.

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rndState = { fn: mulberry32(42) };
const rnd = () => rndState.fn();
const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
const between = (lo, hi) => lo + rnd() * (hi - lo);
const round100 = (v) => Math.round(v / 100) * 100;

export const MONTHS = ["2025-07", "2025-08", "2025-09", "2025-10", "2025-11", "2025-12", "2026-01", "2026-02", "2026-03", "2026-04", "2026-05", "2026-06"];
const SEGMENTS = ["Enterprise", "Mid-Market", "Commercial"];
const REGIONS = ["AMER", "EMEA", "APAC"];
const STAGES = ["Lead", "Qualified", "Proposal", "Negotiation", "Closed Won"];
const INDUSTRIES = ["Financial Services", "Healthcare", "Technology", "Retail", "Manufacturing", "Public Sector"];
const PRODUCTS = ["SSE Platform", "SWG", "CASB", "ZTNA", "DLP Add-on"];
const FIRST = ["Ava", "Liam", "Maya", "Noah", "Zoe", "Ethan", "Ines", "Kai", "Lena", "Marco", "Priya", "Tomás", "Yuki", "Sara", "Omar", "Nina", "Felix", "Dana", "Ravi", "Elsa", "Hugo", "Mei", "Jon", "Aisha"];
const LAST = ["Rivera", "Chen", "Okafor", "Novak", "Silva", "Haddad", "Kim", "Dubois", "Petrov", "Moreau", "Singh", "Costa", "Tanaka", "Lindqvist", "Farouk", "Rossi", "Weber", "Cohen", "Iyer", "Berg", "Marin", "Wu", "Hale", "Khan"];
const COMPANIES = ["Vertex", "Northwind", "Apex", "Halcyon", "Ironclad", "Bluepeak", "Solstice", "Quantia", "Meridian", "Corvus", "Atlas", "Zephyr", "Novara", "Pinnacle", "Crestline", "Orion", "Vanguard", "Summit", "Beacon", "Cascade", "Redwood", "Falcon", "Titan", "Aurora"];
const SUFFIX = ["Systems", "Health", "Bank", "Retail Group", "Industries", "Labs", "Logistics", "Insurance", "Media", "Energy"];

const QUOTA = { Enterprise: 1400000, "Mid-Market": 900000, Commercial: 600000 };
const ARPA = { Enterprise: 125000, "Mid-Market": 48000, Commercial: 18000 };
const DEAL = { Enterprise: [80000, 420000], "Mid-Market": [30000, 140000], Commercial: [8000, 45000] };

export const OPENING_ARR = 24000000;

function genReps() {
  return FIRST.map((f, i) => ({
    rep_id: `REP-${String(i + 1).padStart(2, "0")}`,
    name: `${f[0]}. ${LAST[i]}`,
    segment: SEGMENTS[i % 3],
    region: REGIONS[Math.floor(i / 3) % 3],
    tenure_months: Math.floor(between(4, 60)),
    quota: QUOTA[SEGMENTS[i % 3]],
  }));
}

function genAccounts() {
  const dim_account = [];
  for (let i = 0; i < 72; i++) {
    const segment = SEGMENTS[i % 3];
    const cohortQ = pick(["FY24-Q1", "FY24-Q2", "FY24-Q3", "FY24-Q4", "FY25-Q1", "FY25-Q2", "FY25-Q3", "FY25-Q4"]);
    dim_account.push({
      account_id: `ACC-${String(i + 1).padStart(3, "0")}`,
      name: `${COMPANIES[i % 24]} ${SUFFIX[i % 10]}`,
      segment,
      region: pick(REGIONS),
      arpa: round100(ARPA[segment] * between(0.6, 1.5)),
      industry: pick(INDUSTRIES),
      cohort: cohortQ,
    });
  }

  return dim_account;
}

// fact_bookings + fact_stage_history — one row per deal / stage transition
function genBookings(dim_rep, dim_account) {
  const fact_bookings = [];
  const fact_stage_history = [];
  for (let i = 0; i < 520; i++) {
    const rep = pick(dim_rep);
    const candidates = dim_account.filter((a) => a.segment === rep.segment);
    const acc = pick(candidates.length ? candidates : dim_account);
    const month = pick(MONTHS);
    const r = rnd();
    const status = r < 0.3 ? "won" : r < 0.48 ? "lost" : "open";
    const stage = status === "won" ? "Closed Won" : status === "lost" ? "Closed Lost" : STAGES[Math.floor(rnd() * 4)];
    const [lo, hi] = DEAL[rep.segment];
    const amount = round100(between(lo, hi));
    const cycle = status !== "open" ? Math.floor(between(20, rep.segment === "Enterprise" ? 160 : 90)) : null;
    const created = `${month}-${String(Math.floor(between(1, 28))).padStart(2, "0")}`;
    const deal_id = `OPP-${String(1000 + i)}`;
    fact_bookings.push({
      deal_id, account_id: acc.account_id, account_name: acc.name, rep_id: rep.rep_id, rep_name: rep.name,
      segment: rep.segment, region: rep.region, stage, status, amount,
      is_won: status === "won", month, created_at: created,
      closed_at: cycle ? created : null, sales_cycle_days: cycle,
    });
    const reached = stage === "Closed Lost" ? Math.floor(between(1, 4)) : STAGES.indexOf(stage) === -1 ? 4 : STAGES.indexOf(stage);
    let prev = null;
    for (let s = 0; s <= (stage === "Closed Won" ? 4 : reached); s++) {
      const to = stage === "Closed Lost" && s === reached ? "Closed Lost" : STAGES[s];
      fact_stage_history.push({
        deal_id, from_stage: prev, to_stage: to,
        entered_at: created, days_in_prev_stage: prev ? Math.floor(between(3, 40)) : null,
      });
      prev = to;
    }
  }

  return { fact_bookings, fact_stage_history };
}

// fact_arr_movement — one row per ARR change event
function genArrMovements(dim_account) {
  const fact_arr_movement = [];
  let mid = 1;
  for (const month of MONTHS) {
    const growth = 1 + MONTHS.indexOf(month) * 0.04;
    const mk = (type, count, lo, hi, sign = 1) => {
      for (let i = 0; i < count; i++) {
        const acc = pick(dim_account);
        fact_arr_movement.push({
          movement_id: `MOV-${String(mid++).padStart(4, "0")}`,
          account_id: acc.account_id, account_name: acc.name,
          segment: acc.segment, region: acc.region, type,
          amount: sign * round100(between(lo, hi) * growth),
          effective_date: `${month}-${String(Math.floor(between(1, 28))).padStart(2, "0")}`,
          month,
        });
      }
    };
    mk("new", Math.floor(between(6, 11)), 15000, 120000);
    mk("expansion", Math.floor(between(4, 9)), 8000, 60000);
    mk("contraction", Math.floor(between(2, 5)), 4000, 25000, -1);
    mk("churn", Math.floor(between(1, 4)), 10000, 70000, -1);
  }

  return fact_arr_movement;
}

const COST_CENTERS = {
    Sales: { accounts: ["Salaries & Commissions", "Travel & Events", "Sales Tools"], base: 620000 },
    Marketing: { accounts: ["Demand Gen Programs", "Brand & Content", "Marketing Tech"], base: 340000 },
    "Customer Success": { accounts: ["CS Salaries", "Onboarding Services"], base: 210000 },
    Support: { accounts: ["Support Salaries", "Support Tooling"], base: 130000 },
    "R&D": { accounts: ["Engineering Salaries", "Cloud Infrastructure", "Dev Tooling"], base: 540000 },
    "G&A": { accounts: ["Finance & Legal", "Facilities", "Corporate IT"], base: 190000 },
  };
const VENDORS = ["ADP Payroll", "AWS", "Salesforce", "LinkedIn Ads", "Gong", "WeWork", "Deloitte", "Zendesk", "Marketo", "Concur", "Datadog", "Okta"];

// fact_gl — one row per journal line
function genGL() {
  const fact_gl = [];
  let gid = 1;
  for (const period of MONTHS) {
    const growth = 1 + MONTHS.indexOf(period) * 0.025;
    for (const [cc, cfg] of Object.entries(COST_CENTERS)) {
      for (const account of cfg.accounts) {
        const lines = Math.floor(between(2, 5));
        const target = (cfg.base / cfg.accounts.length) * growth * between(0.85, 1.18);
        for (let l = 0; l < lines; l++) {
          fact_gl.push({
            entry_id: `JE-${String(gid++).padStart(5, "0")}`, period, cost_center: cc, account,
            account_type: "opex", amount: round100(target / lines),
            vendor: pick(VENDORS), memo: `${account} — ${period}`,
          });
        }
      }
    }
    const rev = 2450000 * growth;
    fact_gl.push({ entry_id: `JE-${String(gid++).padStart(5, "0")}`, period, cost_center: "Revenue", account: "Subscription Revenue", account_type: "revenue", amount: round100(rev * 0.92), vendor: null, memo: `Subscription rev recognition ${period}` });
    fact_gl.push({ entry_id: `JE-${String(gid++).padStart(5, "0")}`, period, cost_center: "Revenue", account: "Services Revenue", account_type: "revenue", amount: round100(rev * 0.08), vendor: null, memo: `Services rev recognition ${period}` });
  }

  return fact_gl;
}

// fact_invoice_line — one row per invoice line
function genInvoiceLines(dim_account) {
  const fact_invoice_line = [];
  let inv = 1, lid = 1;
  for (const acc of dim_account) {
    const nInv = Math.floor(between(2, 5));
    for (let i = 0; i < nInv; i++) {
      const invoice_id = `INV-${String(2000 + inv++)}`;
      const period = pick(MONTHS);
      const nLines = Math.floor(between(1, 4));
      for (let l = 0; l < nLines; l++) {
        const qty = Math.floor(between(1, acc.segment === "Enterprise" ? 12 : 5));
        fact_invoice_line.push({
          invoice_id, account_id: acc.account_id, account_name: acc.name,
          line_id: `LN-${String(lid++).padStart(5, "0")}`,
          product: pick(PRODUCTS), period, month: period,
          quantity: qty, amount: round100((acc.arpa / 4 / nLines) * between(0.7, 1.3)),
          segment: acc.segment, region: acc.region, cohort: acc.cohort,
        });
      }
    }
  }

  return fact_invoice_line;
}

function genPlan() {
  const plan = [];
  for (const period of MONTHS) {
    const growth = 1 + MONTHS.indexOf(period) * 0.025;
    for (const [cc, cfg] of Object.entries(COST_CENTERS)) {
      plan.push({ period, cost_center: cc, account_type: "opex", plan_amount: round100(cfg.base * growth) });
    }
    plan.push({ period, cost_center: "Revenue", account_type: "revenue", plan_amount: round100(2500000 * growth) });
  }

  return plan;
}

export function generateSampleData() {
  rndState.fn = mulberry32(42); // deterministic: identical output on every call
  const dim_rep = genReps();
  const dim_account = genAccounts();
  const { fact_bookings, fact_stage_history } = genBookings(dim_rep, dim_account);
  const fact_arr_movement = genArrMovements(dim_account);
  const fact_gl = genGL();
  const fact_invoice_line = genInvoiceLines(dim_account);
  const plan = genPlan();
  return { dim_rep, dim_account, fact_bookings, fact_stage_history, fact_arr_movement, fact_gl, fact_invoice_line, plan };
}
